import React, { useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  ColumnLayout,
  Container,
  CopyToClipboard,
  ExpandableSection,
  Header,
  Link,
  SegmentedControl,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import MarkdownRenderer from '../../utils/markdownRenderer';
import {
  CATEGORY_STATE,
  HUMAN_VERIFY_ROLE,
  OUTCOME,
  advisoryDetails,
  attentionChecks,
  countChecks,
  describeCounts,
  firstLine,
  groupCategoriesByState,
  groupCategoryChecks,
  isHumanVerification,
  readIsfrRecord,
} from './reviewRecord';
import {
  BulletList, CheckRow, Marked, NOT_APPLICABLE_TEXT, agentReviewText, categoryStatus, checkName, checkStatus, checksNotSavedText, preWrap,
} from './checkDisplay';
import { categoryTabId } from './CategoryTab';
import { WORTH_ANCHOR, openTabAtTop, scrollToEntry } from './navigation';
import { FLAGS_TAB_ID } from './FlagsTab';
import { readInputs } from './requestData';
import { formatValue, inputIndicator } from './checkEvidence';
import {
  MARK_EMOJI, ROLE_VIEWS, approvalsCheck, buildReviewBlock, firstSentence, reviewBlockText, roleCategories, roleCountedCategories,
  routingNote,
} from './reviewBlock';

export const REQUEST_DETAILS_TAB_ID = 'isfr-request-details';

/**
 * ISFR's Approval Summary tab.
 *
 * Renders what the ISFR agent persists, in the order a reviewer needs it: the
 * final decision and the checks the chosen view counts; the review block and
 * whether each submitted input could be read; in the PSA view, the review the
 * PSA owns; then a breakdown of every review category naming the checks behind
 * its state, ending with the flags; last, the Contractor view's non-blocking
 * flags in full, or the agent's executive summary. Whether the inputs could be
 * read and what the review found are kept apart, since an input that could not
 * be read stops the checks that depend on it. Each category, the flags, the
 * request's inputs and the reviewer breakdown have their own tabs. Every value
 * comes from the record; a section whose data the record does not carry says
 * so rather than rendering empty.
 */

const VERDICT_COLOR = {
  recommend_approval: 'green',
  recommend_rejection: 'red',
  needs_review: 'blue',
};

// What kind of result this is, where the final decision alone would mislead.
// A request with a substantive problem gets no callout: the final decision,
// the executive summary and the category breakdown already say what is wrong.
const OutcomeAlert = ({ outcome, hasCategories }) => {
  switch (outcome.kind) {
    case OUTCOME.CLEAN_PENDING:
      return (
        <Alert type="success" header="Passed everything we automate">
          Nothing substantive was found. What remains is human verification:{' '}
          {outcome.pendingHuman.join(', ')}. The decision reads Recommend Rejection
          because those items block approval until a human verifies them.
        </Alert>
      );
    case OUTCOME.CLEAN_ROUTED_TO_HUMAN:
      return (
        <Alert type="info" header="Every automated check passed">
          Automated approval is off for this release, so a human makes the final
          call. There is no error to resolve.
        </Alert>
      );
    case OUTCOME.REVIEW_INCOMPLETE:
      return (
        <Alert type="warning" header="The review did not complete">
          No decision is asserted. Resolve the checks below before judging this request.
        </Alert>
      );
    case OUTCOME.APPROVED:
      return (
        <Alert type="success" header="Every check passed">
          Including the ones a human normally verifies.
        </Alert>
      );
    case OUTCOME.PROBLEM_FOUND:
      return null;
    default:
      return (
        <Alert type="info" header="Result type not stated">
          {hasCategories
            ? 'The category results do not support a clean or a problem reading, so this view does not frame the outcome. Use the executive summary below.'
            : 'This record carries no readable category results, so this view does not frame the outcome. Use the executive summary below.'}
        </Alert>
      );
  }
};

// One line of Validations Performed: its mark and label, then what it names.
const SummaryLine = ({
  type, label, testId, children,
}) => (
  <Box variant="small" display="block" padding={{ top: 'xxs' }}>
    <div data-testid={testId}>
      <StatusIndicator type={type}>
        <Box variant="strong" display="inline" fontSize="body-s">{`${label}: `}</Box>
        {/* An info mark would color its text like a link; that line keeps plain text. */}
        <Box display="inline" fontSize="body-s" color={type === 'info' ? 'text-body-default' : 'inherit'}>{children}</Box>
      </StatusIndicator>
    </div>
  </Box>
);

// Checks by name, each a link to where the check is shown in full (`follow`),
// with its kind when the line holds more than one kind.
const NamedChecks = ({ entries, withKind, follow }) => entries.map((entry, i) => (
  <React.Fragment key={`${entry.category.key}-${entry.check.id}`}>
    {i > 0 && ', '}
    {follow
      ? <Link fontSize="body-s" onFollow={() => follow(entry)}>{checkName(entry.check)}</Link>
      : checkName(entry.check)}
    {withKind && ` (${entry.kind})`}
  </React.Fragment>
));

// Where a named check's link leads: the check on its category's tab, or, for a
// flag in the Contractor view, its entry further down this page.
const toCheckTab = (onOpenTab) => (onOpenTab
  ? ({ check, category }) => onOpenTab(categoryTabId(category.key), check.id)
  : null);
const toWorthEntry = ({ check }) => scrollToEntry(WORTH_ANCHOR, check.id);

// The role's own checks: the contractor's in the Contractor view, the PSA's in the
// PSA view, with the categories counted named beneath. The checks not automated
// yet are left to the Human Verification tab, which one line points to.
const ValidationsPerformed = ({ view, role, onOpenTab }) => {
  const { checks } = view;
  const categories = roleCountedCategories(view, role);
  const counts = countChecks(categories, checks);
  if (!counts) {
    return (
      <Box color="text-body-secondary">
        {view.rollup.status === 'ok'
          ? checksNotSavedText(checks.status)
          : 'This record carries no category results to count.'}
      </Box>
    );
  }
  const text = describeCounts(counts);
  const named = attentionChecks(categories, checks);
  const hv = categories.find(isHumanVerification);
  const unnamed = counts.noResult > 0 ? `${named.needed.length > 0 ? ', and ' : ''}${counts.noResult} with no stored result` : '';
  const needsYou = named.needed.length > 0 || unnamed;
  const worthOnPage = role === ROLE_VIEWS[0].id;
  const approvals = approvalsCheck(view);
  const psaAmount = approvals && typeof approvals.evidence.amount === 'number' ? formatValue(approvals.evidence.amount, 'money') : '';
  return (
    <>
      <Box variant="h3">{text.passed}</Box>
      <SummaryLine type={needsYou ? 'error' : 'success'} label="Automated checks requiring your attention" testId="isfr-requires-attention">
        {needsYou ? <NamedChecks entries={named.needed} withKind follow={toCheckTab(onOpenTab)} /> : 'none'}
        {`${unnamed}.`}
      </SummaryLine>
      {counts.routed > 0 && (
        <SummaryLine type="info" label="Proceed to PSA" testId="isfr-proceed-to-psa">
          {psaAmount ? `Requested ${psaAmount}, which needs PSA approval. ` : 'This request needs PSA approval. '}
          After your review, reassign to the PSA.
        </SummaryLine>
      )}
      {counts.notAutomated > 0 && hv && (
        <SummaryLine type="pending" label="Not automated yet" testId="isfr-not-automated">
          {`${counts.notAutomated} ${counts.notAutomated === 1 ? 'check' : 'checks'}, `}
          {onOpenTab
            ? <Link fontSize="body-s" onFollow={() => onOpenTab(categoryTabId(hv.key))}>{`see ${hv.label}`}</Link>
            : `see ${hv.label}`}
          .
        </SummaryLine>
      )}
      {named.worthVerifying.length > 0 && (
        <SummaryLine type="warning" label="Non-blocking, worth verifying" testId="isfr-worth-verifying">
          <NamedChecks entries={named.worthVerifying} follow={worthOnPage ? toWorthEntry : toCheckTab(onOpenTab)} />
          .
        </SummaryLine>
      )}
      <Box variant="small" color="text-body-secondary" display="block">
        {`Counts ${categories.map((c) => c.label).join(', ')}.`}
      </Box>
    </>
  );
};

// Whether each submitted input is present, the right type and readable, before
// any review of its content. Request Details has each input's full state.
const SubmittedInputs = ({ inputs, onOpenTab }) => (
  <div data-testid="isfr-submitted-inputs">
    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
      <Box variant="awsui-key-label">Submitted inputs</Box>
      {onOpenTab && (
        <Link fontSize="body-s" onFollow={() => onOpenTab(REQUEST_DETAILS_TAB_ID)}>View request details</Link>
      )}
    </SpaceBetween>
    {inputs.status === 'ok' ? (
      <SpaceBetween direction="horizontal" size="l">
        {inputs.inputs.map((input) => (
          <StatusIndicator
            key={input.key}
            type={inputIndicator(input.kind, input.enforced)}
          >
            {`${input.label}: ${input.short}`}
          </StatusIndicator>
        ))}
      </SpaceBetween>
    ) : (
      <Box color="text-body-secondary">Input states are not saved for this request.</Box>
    )}
  </div>
);

// One line of the review block. A check reads "Path OK? ✅ (note)", a
// criterion "Label: ✅ : why", a context line "Label: text".
const BlockLine = ({ line }) => {
  const check = line.label.endsWith('?');
  let text = null;
  if (line.text) text = check ? `(${line.text})` : `${line.mark ? ': ' : ' '}${line.text}`;
  return (
    <Box padding={{ left: line.indent ? 'l' : 'n', bottom: 'xxs' }}>
      <span style={{ fontWeight: 700 }}>{check ? line.label : `${line.label}:`}</span>
      {line.mark && ` ${MARK_EMOJI[line.mark]} `}
      {text && <span style={preWrap}><Marked text={text} /></span>}
    </Box>
  );
};

// The review in the format reviewers know from the Innovation Sandbox Reviews
// agent, one line per check or criterion (reviewBlock.js says where each mark
// comes from), with the Context section first when something needs flagging.
const ReviewBlock = ({ groups }) => (
  <div data-testid="isfr-review-block">
    <SpaceBetween size="s">
      {groups.map((group) => (
        <div key={group.key} data-testid={`isfr-review-block-${group.key}`}>
          {group.key === 'context' && <Box variant="awsui-key-label">Context</Box>}
          {group.lines.map((line, i) => <BlockLine key={i} line={line} />)}
        </div>
      ))}
    </SpaceBetween>
  </div>
);

// Over the PSA review bar, the review format's routing step, under the decision.
const RoutingNote = ({ routing, onRoleChange }) => (
  <Box variant="small" display="block" padding={{ top: 'xxs' }}>
    <div data-testid="isfr-routing-note">
      <Box variant="strong" display="inline" fontSize="body-s">{`${routing.label}: `}</Box>
      {routing.text}
      {' '}
      <Link fontSize="body-s" onFollow={() => onRoleChange('psa')}>Expand the PSA review here</Link>
    </div>
  </Box>
);

const ValidationSummary = ({
  view, inputs, groups, routing, role, onRoleChange, onOpenTab,
}) => (
  <Container
    header={(
      <Header
        variant="h2"
        actions={(
          <SpaceBetween direction="horizontal" size="s" alignItems="center">
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <Box variant="awsui-key-label">View as</Box>
              <SegmentedControl
                label="View as"
                selectedId={role}
                onChange={({ detail }) => onRoleChange(detail.selectedId)}
                options={ROLE_VIEWS.map((r) => ({ id: r.id, text: r.text }))}
              />
            </SpaceBetween>
            <CopyToClipboard
              variant="button"
              copyButtonText="Copy review summary"
              copySuccessText="Review summary copied"
              copyErrorText="Review summary failed to copy"
              textToCopy={reviewBlockText(groups, view.verdictLabel, routing)}
            />
          </SpaceBetween>
        )}
      >
        {`${(ROLE_VIEWS.find((r) => r.id === role) || ROLE_VIEWS[0]).text} view: Validation Summary`}
      </Header>
    )}
  >
    <SpaceBetween size="m">
      <ColumnLayout columns={2} variant="text-grid">
        <div>
          <Box variant="awsui-key-label">Final Decision</Box>
          {view.verdict ? (
            <Badge color={VERDICT_COLOR[view.verdict]}>{view.verdictLabel}</Badge>
          ) : (
            <StatusIndicator type="warning">
              {view.rawVerdict ? `Unrecognised decision: ${view.rawVerdict}` : 'No decision recorded'}
            </StatusIndicator>
          )}
          {routing && <RoutingNote routing={routing} onRoleChange={onRoleChange} />}
        </div>
        <div data-testid="isfr-validations-performed">
          <Box variant="awsui-key-label">
            {`${(ROLE_VIEWS.find((r) => r.id === role) || ROLE_VIEWS[0]).text} Validations Performed`}
          </Box>
          <ValidationsPerformed view={view} role={role} onOpenTab={onOpenTab} />
        </div>
      </ColumnLayout>
      <ReviewBlock groups={groups} />
      <SubmittedInputs inputs={inputs} onOpenTab={onOpenTab} />
      <OutcomeAlert outcome={view.outcome} hasCategories={view.rollup.categories.length > 0} />
    </SpaceBetween>
  </Container>
);

const CheckList = ({ rows }) => rows.map((c, i) => (
  <Box key={i}>
    <Box variant="strong">{checkName(c)}</Box>
    {c.message && <Box variant="small" display="block"><span style={preWrap}><Marked text={c.message} /></span></Box>}
  </Box>
));

// An errored check did not complete and is why a request needs review. An
// unhandled FAIL is different: it still fails its category and rejects, but
// only a generic reason explains it, so its details are what to read.
const IncompleteChecks = ({ view }) => {
  const { errored, unhandledFails } = view.incomplete;
  const showErrored = errored.value.length > 0 || errored.status === 'unreadable'
    || Boolean(view.originalRecommendation);
  const showUnhandled = unhandledFails.value.length > 0 || unhandledFails.status === 'unreadable';
  if (!showErrored && !showUnhandled) return null;
  return (
    <SpaceBetween size="s">
      {showErrored && (
        <Alert type="error" header="Resolve these before judging the request">
          <SpaceBetween size="xs">
            {errored.value.length > 0 && <Box>These checks did not complete:</Box>}
            <CheckList rows={errored.value} />
            {errored.status === 'unreadable' && (
              <Box>The errored-check records on this row could not be read.</Box>
            )}
            {view.originalRecommendation && (
              <Box variant="small">
                {`The agent's decision before it routed this to a reviewer: ${view.originalRecommendation}`}
              </Box>
            )}
          </SpaceBetween>
        </Alert>
      )}
      {showUnhandled && (
        <Alert type="warning" header="Failed checks with no specific reason">
          <SpaceBetween size="xs">
            <Box>
              These checks failed and count against their category, but no decision
              rule explains them, so the reasons only say that they failed.
              Read their details.
            </Box>
            <CheckList rows={unhandledFails.value} />
            {unhandledFails.status === 'unreadable' && (
              <Box>The unhandled-failure records on this row could not be read.</Box>
            )}
          </SpaceBetween>
        </Alert>
      )}
    </SpaceBetween>
  );
};

// The agent's executive summary, laid out by its labelled sections. Shown
// whole, as the agent wrote it; text the parser cannot lay out renders as
// written instead.
const ExecutiveSummary = ({ view, defaultExpanded }) => {
  const sections = view.executiveSummary;
  let body;
  if (sections) {
    body = (
      <SpaceBetween size="s">
        {sections.map((section, i) => (
          <div key={i}>
            <Box>
              <Box variant="strong" display="inline">{`${section.label}:`}</Box>
              {section.text && <span style={preWrap}><Marked text={` ${section.text}`} /></span>}
            </Box>
            {section.items.length > 0 && <BulletList items={section.items} />}
          </div>
        ))}
      </SpaceBetween>
    );
  } else if (view.narrative) {
    body = <MarkdownRenderer content={view.narrative} />;
  } else {
    body = <Box color="text-body-secondary">No executive summary was recorded for this run.</Box>;
  }
  return (
    <ExpandableSection
      variant="container"
      defaultExpanded={defaultExpanded}
      headerText="Executive Summary"
      headerDescription="The agent's own account of this result."
    >
      <div data-testid="isfr-executive-summary">{body}</div>
    </ExpandableSection>
  );
};

// The Contractor view closes on its flags in full, in place of the executive
// summary: none changed the verdict, but each is worth a look. Validations
// Performed links each one here.
const WorthVerifying = ({ entries, checks, onOpenTab }) => {
  const nameOf = (id) => (checks.byId.has(id) ? checkName(checks.byId.get(id)) : 'A check not shown on this record');
  return (
    <Container
      header={(
        <Header
          variant="h2"
          description="These are not blockers: they did not change the agent's decision, but each is worth a look before you decide."
        >
          Non-blocking, worth verifying
        </Header>
      )}
    >
      <div data-testid="isfr-worth-verifying-section">
        <SpaceBetween size="l">
          {entries.map(({ check, category }) => (
            <div key={`${category.key}-${check.id}`} {...{ [WORTH_ANCHOR]: check.id }} data-testid="isfr-worth-verifying-item">
              <CheckRow
                check={check}
                nameOf={nameOf}
                context={(
                  <Box variant="small" color="text-body-secondary">
                    {'Relates to '}
                    {onOpenTab
                      ? <Link fontSize="body-s" onFollow={() => onOpenTab(categoryTabId(category.key), check.id)}>{category.label}</Link>
                      : category.label}
                  </Box>
                )}
              />
            </div>
          ))}
        </SpaceBetween>
      </div>
    </Container>
  );
};

const named = (checks) => checks.map(checkName).join(', ');

// One labelled line of a category's breakdown.
const Line = ({ label, children }) => (
  <Box variant="small" display="block" padding={{ left: 'l', top: 'xxs' }}>
    <Box variant="strong" display="inline" fontSize="body-s">{`${label}: `}</Box>
    <span style={preWrap}>{typeof children === 'string' ? <Marked text={children} /> : children}</span>
  </Box>
);

// A category's state and the checks behind it, by name. Failed checks carry the
// first line of their details; the category's tab has every check in full.
const CategoryLine = ({ category, checks, onOpenTab }) => {
  const status = categoryStatus(category);
  const humanVerify = category.role === HUMAN_VERIFY_ROLE;
  const groups = checks.status === 'ok' ? groupCategoryChecks(category, checks.byId) : null;
  return (
    <div data-testid={`isfr-category-line-${category.key}`}>
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <StatusIndicator type={status.type}>
          <Box variant="strong" display="inline">{category.label}</Box>
          {` · ${status.text}`}
        </StatusIndicator>
        {onOpenTab && (
          <Link onFollow={() => onOpenTab(categoryTabId(category.key))}>View checks</Link>
        )}
      </SpaceBetween>
      {groups ? (
        <>
          {advisoryDetails(category, checks.byId).map((detail, i) => (
            <Box key={`detail-${i}`} padding={{ left: 'l', top: 'xxs' }}>
              <StatusIndicator type="warning"><Marked text={detail} /></StatusIndicator>
            </Box>
          ))}
          {groups.failed.map((c) => (
            <Line key={c.id} label={`Failed, ${checkName(c)}`}>{firstLine(c.details)}</Line>
          ))}
          {groups.other.map((c) => (
            <Line key={c.id} label={`${checkStatus(c.result, category.role).text}, ${checkName(c)}`}>
              {firstLine(c.details)}
            </Line>
          ))}
          {groups.incomplete.length > 0 && (
            <Line label={humanVerify ? 'To verify by hand' : 'Not fully reviewed (not a pass)'}>
              {named(groups.incomplete)}
            </Line>
          )}
          {groups.assisting.length > 0 && (
            <Line label="The agent's review (assists you, not a result)">
              {groups.assisting.map((c) => `${checkName(c)}: ${agentReviewText(c.result)}`).join(', ')}
            </Line>
          )}
          {groups.flagged.length > 0 && (
            <Line label="Flagged (did not affect the decision)">{named(groups.flagged)}</Line>
          )}
          {groups.passed.length > 0 && <Line label="Passed">{named(groups.passed)}</Line>}
          {groups.notApplicable.length > 0 && (
            <Line label={NOT_APPLICABLE_TEXT}>{named(groups.notApplicable)}</Line>
          )}
          {groups.missing.length > 0 && (
            <Line label="No stored result">
              {`${groups.missing.length} ${groups.missing.length === 1 ? 'check' : 'checks'} in this category`}
            </Line>
          )}
        </>
      ) : null}
    </div>
  );
};

// The flags, last in the breakdown: named here, in full on their own tab.
const FlagsLine = ({ view, onOpenTab }) => {
  const { flags, checks } = view;
  const triggered = checks.status === 'ok' && flags.length > 0;
  return (
    <div data-testid="isfr-flags-line">
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <StatusIndicator type={triggered ? 'warning' : 'info'}>
          <Box variant="strong" display="inline">Flags to Keep in Mind</Box>
          {checks.status === 'ok' && ` · ${triggered ? 'Worth your attention' : 'No flags triggered'}`}
        </StatusIndicator>
        {onOpenTab && <Link onFollow={() => onOpenTab(FLAGS_TAB_ID)}>View flags</Link>}
      </SpaceBetween>
      {triggered && <Line label="Flagged">{named(flags)}</Line>}
    </div>
  );
};

// The heading over each group of categories in the breakdown.
const groupLabel = (group) => {
  if (group.key === 'to_verify') return 'Human verification required';
  if (group.key === 'passed') return 'Passed';
  if (group.key === 'not_applicable') return NOT_APPLICABLE_TEXT;
  const failed = group.categories.filter((c) => c.state === CATEGORY_STATE.FAILED).length;
  if (failed === group.categories.length) return 'Failed';
  return failed > 0 ? 'Failed or not fully reviewed' : 'Not fully reviewed';
};

const CategoryBreakdown = ({ view, onOpenTab }) => {
  const { rollup, checks } = view;
  const { unmapped, infrastructure, invalidResults } = rollup.anomalies;
  // Named where the record names the check; a result the record cannot name is
  // counted rather than shown by its id.
  const describe = (ids) => {
    const names = ids.filter((id) => checks.byId.has(id)).map((id) => checkName(checks.byId.get(id)));
    const unnamed = ids.length - names.length;
    return [...names, ...(unnamed ? [`${unnamed} unnamed`] : [])].join(', ');
  };
  const anomalies = [
    unmapped.length > 0 && `in no category: ${describe(unmapped)}`,
    infrastructure.length > 0 && `infrastructure results: ${describe(infrastructure)}`,
    invalidResults.length > 0 && `unrecognised results: ${describe(invalidResults)}`,
  ].filter(Boolean);
  return (
    <Container
      header={(
        <Header
          variant="h2"
          description="Each category is judged on its own, and the decision is derived from all of them. Each has its own tab with every check in full."
        >
          By category
        </Header>
      )}
    >
      {rollup.status === 'ok' ? (
        <SpaceBetween size="m">
          {groupCategoriesByState(rollup.categories).map((group) => (
            <div key={group.key} data-testid={`isfr-category-group-${group.key}`}>
              <SpaceBetween size="s">
                <Box variant="awsui-key-label">{groupLabel(group)}</Box>
                {group.categories.map((category) => (
                  <CategoryLine key={category.key} category={category} checks={checks} onOpenTab={onOpenTab} />
                ))}
              </SpaceBetween>
            </div>
          ))}
          <FlagsLine view={view} onOpenTab={onOpenTab} />
          {checks.status !== 'ok' && (
            <Box variant="small" color="text-body-secondary">
              {`${checksNotSavedText(checks.status)} Until then, only each category's state is shown.`}
            </Box>
          )}
          {anomalies.length > 0 && (
            <Alert type="warning" header="Results the review could not place">
              {`These check results could not be counted toward a category, which routes the request to a human. ${anomalies.join('; ')}.`}
            </Alert>
          )}
        </SpaceBetween>
      ) : (
        <Box color="text-body-secondary">
          {rollup.status === 'unreadable'
            ? 'The category results on this record could not be read.'
            : 'This record carries no category results. Records validated before the agent stored them look like this; validating the request again stores them.'}
        </Box>
      )}
    </Container>
  );
};

// One check in the role view's summary: its result, the first sentence of what it
// found, and a link to the check in its category's tab, where the full detail is.
const GlanceRow = ({ check, category, onOpenTab }) => {
  const status = checkStatus(check.result, category.role, category.advisory);
  const summary = firstSentence(check.details);
  return (
    <Box padding={{ left: 'l', top: 'xxs' }}>
      <StatusIndicator type={status.type}>
        <Box variant="strong" display="inline" fontSize="body-s">{checkName(check)}</Box>
        {` · ${status.text}`}
      </StatusIndicator>
      <Box variant="small" display="block" padding={{ left: 'l' }}>
        {summary && <span style={preWrap}><Marked text={`${summary} `} /></span>}
        {onOpenTab && (
          <Link fontSize="body-s" onFollow={() => onOpenTab(categoryTabId(category.key), check.id)}>Details</Link>
        )}
      </Box>
    </Box>
  );
};

// The checks a role view summarises in one line: passed ones by name, each a link.
const NamedLinks = ({ label, checks: rows, category, onOpenTab }) => (rows.length === 0 ? null : (
  <Box variant="small" display="block" padding={{ left: 'l', top: 'xxs' }}>
    <Box variant="strong" display="inline" fontSize="body-s">{`${label}: `}</Box>
    {rows.map((c, i) => (
      <React.Fragment key={c.id || i}>
        {i > 0 && ', '}
        {onOpenTab
          ? <Link fontSize="body-s" onFollow={() => onOpenTab(categoryTabId(category.key), c.id)}>{checkName(c)}</Link>
          : checkName(c)}
      </React.Fragment>
    ))}
  </Box>
));

// The review a role view adds below the review block, at a glance: each category's
// state, then the checks that need attention with what they found, then the rest by
// name. Every check links to its row in the category's tab, which has it in full.
const RoleSections = ({
  categories, checks, roleText, onOpenTab,
}) => (
  categories.length === 0 ? null : (
    <div data-testid="isfr-role-sections">
      <Container
        header={(
          <Header variant="h2" description="Each check links to its full detail in its category's tab.">
            {`${roleText} review`}
          </Header>
        )}
      >
        <SpaceBetween size="m">
          {categories.map((category) => {
            const status = categoryStatus(category);
            const groups = checks.status === 'ok' ? groupCategoryChecks(category, checks.byId) : null;
            return (
              <div key={category.key} data-testid={`isfr-role-section-${category.key}`}>
                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                  <StatusIndicator type={status.type}>
                    <Box variant="strong" display="inline">{category.label}</Box>
                    {` · ${status.text}`}
                  </StatusIndicator>
                  {onOpenTab && <Link onFollow={() => onOpenTab(categoryTabId(category.key))}>View all checks</Link>}
                </SpaceBetween>
                {groups ? (
                  <>
                    {advisoryDetails(category, checks.byId).map((detail, i) => (
                      <Box key={`detail-${i}`} padding={{ left: 'l', top: 'xxs' }}>
                        <StatusIndicator type="warning"><Marked text={detail} /></StatusIndicator>
                      </Box>
                    ))}
                    {[...groups.failed, ...groups.other, ...groups.flagged, ...groups.incomplete, ...groups.assisting]
                      .map((c) => <GlanceRow key={c.id} check={c} category={category} onOpenTab={onOpenTab} />)}
                    <NamedLinks label="Passed" checks={groups.passed} category={category} onOpenTab={onOpenTab} />
                    <NamedLinks label={NOT_APPLICABLE_TEXT} checks={groups.notApplicable} category={category} onOpenTab={onOpenTab} />
                  </>
                ) : (
                  <Box variant="small" color="text-body-secondary" padding={{ left: 'l' }}>{checksNotSavedText(checks.status)}</Box>
                )}
              </div>
            );
          })}
        </SpaceBetween>
      </Container>
    </div>
  )
);

// The view the reviewer picked stays picked for the browser session, across tabs
// and requests: this tab unmounts while another tab is open.
const VIEW_KEY = 'isfr-review-view';
const savedView = () => {
  try {
    const id = window.sessionStorage.getItem(VIEW_KEY);
    return ROLE_VIEWS.some((r) => r.id === id) ? id : ROLE_VIEWS[0].id;
  } catch (e) {
    return ROLE_VIEWS[0].id;
  }
};
const saveView = (id) => {
  try {
    window.sessionStorage.setItem(VIEW_KEY, id);
  } catch (e) {
    // Storage unavailable: the view still switches, it is just not remembered.
  }
};

const ReviewSummary = ({ request, onOpenTab: openTab }) => {
  // In-page links land at the top of the tab they open.
  const onOpenTab = openTab ? openTabAtTop(openTab) : undefined;
  const [role, setRole] = useState(savedView);
  const chooseRole = (id) => {
    setRole(id);
    saveView(id);
  };
  const view = readIsfrRecord(request);
  const roleText = (ROLE_VIEWS.find((r) => r.id === role) || ROLE_VIEWS[0]).text;
  // The Contractor view lists its flags in full where the PSA view has the
  // executive summary. Without per-check results there is nothing to list, and
  // the executive summary is where the flags are, so it stays.
  const flagsInFull = role === ROLE_VIEWS[0].id && view.checks.status === 'ok';
  const worth = flagsInFull ? attentionChecks(roleCountedCategories(view, role), view.checks).worthVerifying : [];
  return (
    <SpaceBetween size="l">
      <ValidationSummary
        view={view}
        inputs={readInputs(request)}
        groups={buildReviewBlock(view, request)}
        routing={routingNote(view)}
        role={role}
        onRoleChange={chooseRole}
        onOpenTab={onOpenTab}
      />
      <RoleSections categories={roleCategories(view, role)} checks={view.checks} roleText={roleText} onOpenTab={onOpenTab} />
      <IncompleteChecks view={view} />
      <CategoryBreakdown view={view} onOpenTab={onOpenTab} />
      {flagsInFull && worth.length > 0 && <WorthVerifying entries={worth} checks={view.checks} onOpenTab={onOpenTab} />}
      {/* Collapsed in the Contractor view when it has no per-check results, open in the PSA view. */}
      {!flagsInFull && <ExecutiveSummary key={role} view={view} defaultExpanded={role !== ROLE_VIEWS[0].id} />}
    </SpaceBetween>
  );
};

export default ReviewSummary;
